import { TERRAIN_DEFS } from '../content/terrain';
import { buildingDef } from '../content/buildings';
import { TILE } from '../sim/constants';
import { screenToWorldX, screenToWorldY } from './projection';
import type { Camera, Viewport } from './projection';
import type { GameState } from '../sim/types';

/*
 * המפה המוקטנת.
 *
 * **תצוגה בלבד.** היא קוראת מהמצב ולעולם לא כותבת אליו, ולכן היא יושבת ב-render/
 * ולא נוגעת בסימולציה בשום צורה. אין כאן RNG, אין זמן, ואין שום החלטה שמשפיעה
 * על משחק.
 *
 * שני דברים כאן אינם קוסמטיים:
 *
 *   1. **הטרשת והעפרות במטמון של פיקסל-לאריח.** ציור 9,216 אריחים בכל פריים הוא
 *      בזבוז מוחלט. במקום זה נבנה קנבס מנותק בגודל `map.w × map.h` **פיקסלים**
 *      (96×96 = 9KB), נכתב בפעימה אחת של `putImageData`, ומצויר כתמונה אחת
 *      מוגדלת. המטמון נבנה מחדש רק כשההפניה למערך הטרשת או לזה של העפרות
 *      משתנה — והסימולציה מעתיקה את מערך העפרות רק בטיק שבו מישהו באמת כרה.
 *      זו הסיבה שהמטמון מזוהה לפי **הפניה** ולא לפי תוכן.
 *
 *   2. **המפה אינה ריבועית בהכרח.** `layout.size` הוא הצלע של הריבוע שמוקצה
 *      למפה, והמפה נכנסת לתוכו לפי היחס שלה וממורכזת בו. מתיחה של מפה 96×48
 *      לריבוע הייתה משקרת על מרחקים — וזה בדיוק מה שמפה מוקטנת אמורה למדוד.
 */

export interface MinimapLayout {
    /** פינת הריבוע שמוקצה למפה, בפיקסלי CSS על קנבס המשחק. */
    readonly x: number;
    readonly y: number;
    /** צלע הריבוע. המפה נכנסת לתוכו לפי היחס שלה. */
    readonly size: number;
}

/** גודל המפה באריחים. כל מה שהמודול צריך לדעת על המפה כדי למפות קואורדינטות. */
export interface MinimapSize {
    readonly w: number;
    readonly h: number;
}

/** המלבן שהמפה תופסת בפועל בתוך ריבוע ה-layout, אחרי שמירת היחס. */
export interface MinimapRect {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    /** פיקסלים לאריח. */
    readonly scale: number;
}

export interface MinimapTerrain extends MinimapSize {
    readonly terrain: Uint8Array;
}

export interface MinimapArgs {
    readonly map: MinimapTerrain;
    readonly ore: Uint16Array;
    readonly state: GameState;
    readonly camera: Camera;
    readonly viewport: Viewport;
    readonly playerColors: readonly string[];
    readonly localPlayer: number;
    /**
     * ערפל קרב. **בלעדיו המפה המוקטנת היא פתרון המשחק** — היא הייתה מציגה את
     * כל צבא היריב ואת כל שדות העפרות מהשנייה הראשונה, וזה בדיוק מה שמבטל את
     * הצורך לסייר.
     */
    readonly fog?: {
        readonly visible: Uint8Array;
        readonly explored: Uint8Array;
        /** מבני האויב שהשחקן זוכר. ראו `render/memory.ts`. */
        readonly remembered?: ReadonlyMap<number, unknown>;
        /** יחידות האויב כפי שהשחקן יודע אותן. אותו מקור כמו שדה הקרב. */
        readonly enemyAlpha?: ReadonlyMap<number, { readonly x: number; readonly y: number }>;
    };
}

/** צלע הנקודה שמסמנת יחידה. באצבע צריך משהו שנראה, לא פיקסל בודד. */
const UNIT_DOT = 3;

/** מבנה קטן חייב עדיין להיראות — 2×2 הוא הרצפה. */
const MIN_BUILDING_PX = 2;

const BACKGROUND = 'rgba(6,10,15,0.92)';
const BORDER = 'rgba(120,140,165,0.55)';
const CAMERA_BOX = 'rgba(235,240,248,0.9)';

/** כמות העפרות שמעליה האריח מצויר בצבע המלא. זהה לסף של `render/terrain`. */
const ORE_RICH = 600;
const ORE_RGB: readonly [number, number, number] = [0xd9, 0xa5, 0x3a];

const TERRAIN_RGB: readonly (readonly [number, number, number])[] = TERRAIN_DEFS.map((def) =>
    hexToRgb(def.color),
);

function hexToRgb(hex: string): [number, number, number] {
    const value = Number.parseInt(hex.slice(1), 16);
    if (!Number.isFinite(value)) return [255, 0, 255];
    return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

/**
 * המלבן שהמפה תופסת בתוך ריבוע ה-layout.
 *
 * **חישוב טהור, בלי קנבס** — וזה מה שמאפשר לבדוק את כל המיפוי בטסט node רגיל
 * בלי לגעת ב-DOM.
 */
export function minimapRect(layout: MinimapLayout, map: MinimapSize): MinimapRect {
    const longest = Math.max(map.w, map.h, 1);
    const scale = layout.size / longest;
    const w = map.w * scale;
    const h = map.h * scale;
    return {
        x: layout.x + (layout.size - w) / 2,
        y: layout.y + (layout.size - h) / 2,
        w,
        h,
        scale,
    };
}

/** קואורדינטת עולם → פיקסל על המפה המוקטנת. */
export function worldToMinimapX(rect: MinimapRect, worldX: number): number {
    return rect.x + (worldX / TILE) * rect.scale;
}

export function worldToMinimapY(rect: MinimapRect, worldY: number): number {
    return rect.y + (worldY / TILE) * rect.scale;
}

/**
 * הקשה על המפה המוקטנת → נקודה בעולם. מחזיר null אם ההקשה מחוץ למפה.
 *
 * **ההופכי המדויק של `worldToMinimap*`** — בלי עיגול, כדי שהמצלמה תנחת בדיוק
 * במקום שהאצבע הצביעה עליו. הקצה נחסם פנימה: `map.w * TILE` הוא כבר האריח
 * שאחרי הקצה.
 */
export function minimapToWorld(
    layout: MinimapLayout,
    screenX: number,
    screenY: number,
    map: MinimapSize,
): { x: number; y: number } | null {
    const rect = minimapRect(layout, map);
    if (rect.w <= 0 || rect.h <= 0) return null;
    if (screenX < rect.x || screenX > rect.x + rect.w) return null;
    if (screenY < rect.y || screenY > rect.y + rect.h) return null;

    return {
        x: clamp(((screenX - rect.x) / rect.scale) * TILE, 0, map.w * TILE - 1),
        y: clamp(((screenY - rect.y) / rect.scale) * TILE, 0, map.h * TILE - 1),
    };
}

function clamp(value: number, min: number, max: number): number {
    return value < min ? min : value > max ? max : value;
}

export function drawMinimap(
    ctx: CanvasRenderingContext2D,
    layout: MinimapLayout,
    args: MinimapArgs,
): void {
    const rect = minimapRect(layout, args.map);
    if (rect.w <= 0 || rect.h <= 0) return;

    ctx.save();

    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

    const cached = terrainImage(args.map, args.ore, args.fog?.explored ?? null);
    if (cached) {
        /*
         * בלי כיבוי ההחלקה, הגדלה מ-96px ל-~130px מטשטשת את גבולות העפרות
         * לכתם. במפה מוקטנת רוצים בלוקים חדים — היא כלי מדידה, לא תמונה.
         */
        const smoothing = ctx.imageSmoothingEnabled;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(cached, rect.x, rect.y, rect.w, rect.h);
        ctx.imageSmoothingEnabled = smoothing;
    }

    /*
     * שני מעברים, האויב קודם: מה ששלי מצויר מעל, כך שגוש תוקף לא מסתיר את
     * המבנה שהוא תוקף. זה כל השימוש ב-`localPlayer` כאן.
     */
    drawSide(ctx, rect, args, false);
    drawSide(ctx, rect, args, true);

    drawCameraBox(ctx, rect, args);

    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1);

    ctx.restore();
}

function drawSide(
    ctx: CanvasRenderingContext2D,
    rect: MinimapRect,
    args: MinimapArgs,
    mine: boolean,
): void {
    for (const building of args.state.buildings) {
        if ((building.owner === args.localPlayer) !== mine) continue;
        const def = buildingDef(building.type);
        // מבנה מצויר מהזיכרון; יחידה נעלמת. אותו כלל כמו בשדה הקרב.
        if (args.fog && !mine) {
            if (args.fog.remembered) {
                if (!args.fog.remembered.has(building.id)) continue;
            } else {
                const tile = building.tileY * args.map.w + building.tileX;
                if (args.fog.explored[tile] !== 1) continue;
            }
        }
        ctx.fillStyle = colorOf(args.playerColors, building.owner);
        ctx.fillRect(
            rect.x + building.tileX * rect.scale,
            rect.y + building.tileY * rect.scale,
            Math.max(MIN_BUILDING_PX, def.w * rect.scale),
            Math.max(MIN_BUILDING_PX, def.h * rect.scale),
        );
    }

    for (const unit of args.state.units) {
        if ((unit.owner === args.localPlayer) !== mine) continue;
        /*
         * **אותו מקור בדיוק כמו שדה הקרב, כולל המיקום.** קודם כאן נקראה `visible`
         * ישירות, כלומר המפה המוקטנת התעלמה מחסד השנייה שנבנה נגד הריצוד — ואותה
         * יחידה הבהבה כאן בתדירות גבוהה יותר מאשר על השדה. שני מקומות שמציירים
         * את אותו דבר לפי שני חוקים הם באג, גם כשכל אחד מהם נכון בנפרד. ומאז
         * שידיעה ישנה מצוירת במקום הזכור, גם הנקודה כאן חייבת לשבת שם — אחרת
         * המפה המוקטנת מסגירה בדיוק את מה שהערפל מסתיר.
         */
        let at: { readonly x: number; readonly y: number } = unit;
        if (args.fog && !mine) {
            if (args.fog.enemyAlpha) {
                const known = args.fog.enemyAlpha.get(unit.id);
                if (!known) continue;
                at = known;
            } else {
                const tile =
                    Math.floor(unit.y / TILE) * args.map.w + Math.floor(unit.x / TILE);
                if (args.fog.visible[tile] !== 1) continue;
            }
        }
        ctx.fillStyle = colorOf(args.playerColors, unit.owner);
        ctx.fillRect(
            worldToMinimapX(rect, at.x) - UNIT_DOT / 2,
            worldToMinimapY(rect, at.y) - UNIT_DOT / 2,
            UNIT_DOT,
            UNIT_DOT,
        );
    }
}

/** המלבן שמסמן מה המצלמה רואה. נחסם לגבולות המפה — בזום רחוק הוא חורג ממנה. */
function drawCameraBox(ctx: CanvasRenderingContext2D, rect: MinimapRect, args: MinimapArgs): void {
    const { camera, viewport } = args;
    if (viewport.width <= 0 || viewport.height <= 0) return;

    const left = clamp(
        worldToMinimapX(rect, screenToWorldX(0, camera, viewport)),
        rect.x,
        rect.x + rect.w,
    );
    const top = clamp(
        worldToMinimapY(rect, screenToWorldY(0, camera, viewport)),
        rect.y,
        rect.y + rect.h,
    );
    const right = clamp(
        worldToMinimapX(rect, screenToWorldX(viewport.width, camera, viewport)),
        rect.x,
        rect.x + rect.w,
    );
    const bottom = clamp(
        worldToMinimapY(rect, screenToWorldY(viewport.height, camera, viewport)),
        rect.y,
        rect.y + rect.h,
    );

    ctx.strokeStyle = CAMERA_BOX;
    ctx.lineWidth = 1;
    ctx.strokeRect(left + 0.5, top + 0.5, Math.max(2, right - left), Math.max(2, bottom - top));
}

function colorOf(playerColors: readonly string[], owner: number): string {
    return playerColors[owner] ?? '#ffffff';
}

/* ------------------------------------------------------------------ */
/* מטמון הרקע                                                          */
/* ------------------------------------------------------------------ */

interface TerrainCache {
    readonly canvas: HTMLCanvasElement;
    terrain: Uint8Array;
    ore: Uint16Array;
    /** ‏`explored` שממנו נבנה הרקע. מתחלף בהפניה רק כשהתגלה שטח חדש. */
    explored: Uint8Array | null;
    w: number;
    h: number;
}

let cache: TerrainCache | null = null;
/*
 * סביבה בלי קנבס אמיתי (happy-dom בטסטים) מחזירה `getContext` ריק. בלי הדגל
 * הזה היינו מנסים לבנות מטמון מחדש בכל פריים ונכשלים בכל פריים.
 */
let cacheUnavailable = false;

/** משחררת את המטמון. לקרוא ביציאה ממשחק, ובטסטים בין מקרים. */
export function resetMinimapCache(): void {
    cache = null;
    cacheUnavailable = false;
}

function terrainImage(
    map: MinimapTerrain,
    ore: Uint16Array,
    explored: Uint8Array | null,
): HTMLCanvasElement | null {
    if (
        cache &&
        cache.terrain === map.terrain &&
        cache.ore === ore &&
        cache.explored === explored &&
        cache.w === map.w &&
        cache.h === map.h
    ) {
        return cache.canvas;
    }
    if (cacheUnavailable) return null;

    const canvas = cache?.canvas ?? createCanvas();
    if (!canvas) {
        cacheUnavailable = true;
        return null;
    }
    canvas.width = map.w;
    canvas.height = map.h;

    const ctx = canvas.getContext('2d');
    if (!ctx || typeof ctx.createImageData !== 'function') {
        cacheUnavailable = true;
        return null;
    }

    const image = ctx.createImageData(map.w, map.h);
    paintTerrain(image.data, map, ore);
    /*
     * שטח שלא נחשף נמחק מהרקע לגמרי — לא מוכהה. **המפה המוקטנת היא המקום שבו
     * ערפל הקרב הכי משנה:** בלעדיו היא מראה את צורת המפה, את כל שדות העפרות ואת
     * הדרך לבסיס האויב מהשנייה הראשונה, וזה מייתר את הסיור עוד לפני שהתחיל.
     */
    if (explored) {
        for (let i = 0; i < explored.length && i * 4 + 3 < image.data.length; i++) {
            if (explored[i] !== 1) image.data[i * 4 + 3] = 0;
        }
    }
    ctx.putImageData(image, 0, 0);

    cache = { canvas, terrain: map.terrain, ore, explored, w: map.w, h: map.h };
    return canvas;
}

function createCanvas(): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    try {
        return document.createElement('canvas');
    } catch {
        return null;
    }
}

/**
 * כותבת פיקסל לאריח לתוך מאגר RGBA.
 *
 * מיוצאת בנפרד מהציור כדי שאפשר יהיה לבדוק אותה בלי קנבס בכלל: היא מקבלת
 * מאגר ומחזירה מאגר, ולא יודעת שקיים DOM.
 */
export function paintTerrain(
    data: Uint8ClampedArray,
    map: MinimapTerrain,
    ore: Uint16Array,
): void {
    const count = map.w * map.h;
    for (let index = 0; index < count; index++) {
        const rgb = TERRAIN_RGB[map.terrain[index] ?? 0] ?? TERRAIN_RGB[0] ?? [255, 0, 255];
        let r = rgb[0];
        let g = rgb[1];
        let b = rgb[2];

        const amount = ore[index] ?? 0;
        if (amount > 0) {
            // ככל שהשדה עשיר יותר כך הזהב שולט. שדה מדולדל נראה מדולדל גם כאן.
            const t = 0.45 + 0.45 * Math.min(1, amount / ORE_RICH);
            r = Math.round(r + (ORE_RGB[0] - r) * t);
            g = Math.round(g + (ORE_RGB[1] - g) * t);
            b = Math.round(b + (ORE_RGB[2] - b) * t);
        }

        const p = index * 4;
        data[p] = r;
        data[p + 1] = g;
        data[p + 2] = b;
        data[p + 3] = 255;
    }
}
